import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EmailMonitor } from '../../../../../types';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { useStore } from '../../../state/zustand-store';
import dayjs from 'dayjs';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Controller, useForm } from 'react-hook-form';
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import GeneralDialog from '../../../../shared/general-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import {
  addEmailMonitor,
  editEmailMonitor,
  UserKeys,
} from '../../../../api/users-api';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { handleErrors } from '../../../../utils/errors';
import * as analytics from '../../../../shared/analytics/analytics.json';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';

interface EmailMonitorDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
}

interface CreateEmailMonitorDialogProps extends EmailMonitorDialogProps {
  dialogType: 'create';
  selectedEmailMonitor: null;
}
interface EditEmailMonitorDialogProps extends EmailMonitorDialogProps {
  dialogType: 'edit';
  selectedEmailMonitor: EmailMonitor;
}

export default function EmailMonitorDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  dialogType,
  selectedEmailMonitor,
}: CreateEmailMonitorDialogProps | EditEmailMonitorDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  interface EmailMonitorForm extends EmailMonitor {
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  }

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm<EmailMonitorForm>({
    defaultValues:
      dialogType === 'create'
        ? {
            listeningUser: '',
            monitoredUser: userEmail,
            dateRange: [
              dayjs().startOf('day').add(1, 'day'),
              dayjs().endOf('day').add(1, 'month').add(1, 'day'),
            ] as DateRange<dayjs.Dayjs>,
            draftMonitoring: 'NONE',
            chatMonitoring: 'NONE',
            fullOutgoing: 'HEADER_ONLY',
            fullIncoming: 'HEADER_ONLY',
          }
        : {
            ...selectedEmailMonitor,
            dateRange: [
              dayjs()
                .year(selectedEmailMonitor.startYear)
                .month(selectedEmailMonitor.startMonth)
                .date(selectedEmailMonitor.startDay),
              dayjs()
                .year(selectedEmailMonitor.endYear)
                .month(selectedEmailMonitor.endMonth)
                .date(selectedEmailMonitor.endDay),
            ] as DateRange<dayjs.Dayjs>,
            requestId: selectedEmailMonitor.listeningUser.split('@')[0],
          },
  });

  const onSubmit = (data: EmailMonitorForm) => {
    if (dialogType === 'create') {
      if (
        isAbleToSeeUIElement(changeLogRoles, UserRoles.AddEmailMonitors.value)
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate({
          listeningUser: data.listeningUser,
          monitoredUser: data.monitoredUser,
          requestId: data.listeningUser.split('@')[0],
          startDay: data.dateRange[0]!.date(),
          startMonth: data.dateRange[0]!.month(),
          startYear: data.dateRange[0]!.year(),
          endDay: data.dateRange[1]!.date(),
          endMonth: data.dateRange[1]!.month(),
          endYear: data.dateRange[1]!.year(),
          draftMonitoring: data.draftMonitoring,
          chatMonitoring: data.chatMonitoring,
          fullIncoming: data.fullIncoming,
          fullOutgoing: data.fullOutgoing,
        });
      }
    } else if (dialogType === 'edit') {
      if (
        isAbleToSeeUIElement(changeLogRoles, UserRoles.EditEmailMonitors.value)
      ) {
        setOpenChangeLogDialog(true);
      } else {
        editMutate({
          listeningUser: data.listeningUser,
          monitoredUser: data.monitoredUser,
          requestId: data.requestId,
          startDay: data.dateRange[0]!.date(),
          startMonth: data.dateRange[0]!.month(),
          startYear: data.dateRange[0]!.year(),
          endDay: data.dateRange[1]!.date(),
          endMonth: data.dateRange[1]!.month(),
          endYear: data.dateRange[1]!.year(),
          draftMonitoring: data.draftMonitoring,
          chatMonitoring: data.chatMonitoring,
          fullIncoming: data.fullIncoming,
          fullOutgoing: data.fullOutgoing,
        });
      }
    }
  };

  const monitorDraftOptions = [
    { value: 'NONE', displayValue: 'Do not monitor drafts' },
    { value: 'HEADER_ONLY', displayValue: 'Draft headers only' },
    { value: 'FULL_MESSAGE', displayValue: 'Full drafts monitoring' },
  ];
  const monitorChatOptions = [
    { value: 'NONE', displayValue: 'Do not monitor chats' },
    { value: 'HEADER_ONLY', displayValue: 'Draft headers only' },
    { value: 'FULL_MESSAGE', displayValue: 'Full chats monitoring' },
  ];

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<EmailMonitor>) =>
      addEmailMonitor(userEmail, data, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Email monitor'),
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.EmailMonitors, userEmail],
      });
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('this email monitor'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.auditing.create_email_monitor,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.auditing,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: editMutate, isPending: editPending } = useMutation({
    mutationFn: (data: Partial<EmailMonitor>) =>
      editEmailMonitor(userEmail, data, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Email monitor'),
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.EmailMonitors, userEmail],
      });

      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('this email monitor'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.auditing.update_email_monitor,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.userDetails,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <GeneralDialog
      open={open}
      maxWidth="md"
      handleClose={() => {
        handleClose();
        reset();
      }}
      title={dialogType === 'create' ? 'Add Monitor' : 'Edit Monitor'}
      dialogContent={
        <>
          <Typography variant="body2">
            The destination user will receive blind carbon copies (Bcc) of all
            incoming and outgoing email messages. Each audited email message is
            sent to the destination user as an email attachment. More
            information can be found{' '}
            <Link
              href="https://developers.google.com/admin-sdk/email-audit/monitor-email"
              target="_blank"
            >
              here
            </Link>
            .
          </Typography>
          <Box sx={{ px: 4, pt: 4 }} component={'form'}>
            <Stack flexDirection={'column'} gap={3}>
              {dialogType === 'create' ? (
                <Controller
                  name={'listeningUser'}
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ width: 400 }}>
                      <SharedAutocomplete
                        sources={['users']}
                        autocompleteValue={field.value}
                        setAutoCompleteValue={field.onChange}
                        label="Destination User"
                      />
                    </Box>
                  )}
                />
              ) : (
                <TextField
                  value={getValues('listeningUser')}
                  disabled
                  label="Destination User"
                  sx={{ width: 400 }}
                />
              )}

              <Controller
                name={'dateRange'}
                control={control}
                rules={{
                  validate: {
                    required: (value) => {
                      if (!value[0]?.isValid() || !value[1]?.isValid()) {
                        return 'Both start and end dates are required';
                      }
                      return true;
                    },
                    futureDate: (value) => {
                      const today = dayjs().startOf('day');
                      const startDate = value[0];

                      if (
                        startDate?.isSame(today, 'day') ||
                        startDate?.isBefore(today)
                      ) {
                        return 'Start date must be in the future';
                      }
                      return true;
                    },
                    endAfterStart: (value) => {
                      if (value[1] && value[0] && value[1].isBefore(value[0])) {
                        return 'End date must be after the start date';
                      }
                      return true;
                    },
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <DateRangePicker
                    {...field}
                    disablePast
                    disableHighlightToday
                    // Disable today's date as the start date
                    shouldDisableDate={(day, position) => {
                      if (position === 'start' || position === 'end') {
                        return day.isSame(dayjs(), 'day');
                      }
                      return false;
                    }}
                    sx={{ width: 400 }}
                    label={'Monitoring Dates'}
                    slots={{ field: SingleInputDateRangeField }}
                    slotProps={{
                      textField: {
                        error: !!error?.message,
                        helperText: error?.message ? error.message : ' ',
                      },
                    }}
                  />
                )}
              />
              <Stack flexDirection={'row'} alignItems={'center'} gap={5}>
                <Typography>Monitor Incoming Messages: </Typography>
                <Stack
                  flexDirection="row"
                  component="label"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography>Headers Only</Typography>
                  <Controller
                    name="fullIncoming"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        onChange={() => {
                          if (field.value === 'FULL_MESSAGE') {
                            setValue('fullIncoming', 'HEADER_ONLY');
                          } else {
                            setValue('fullIncoming', 'FULL_MESSAGE');
                          }
                        }}
                        checked={field.value === 'FULL_MESSAGE'}
                      />
                    )}
                  />
                  <Typography>Full Messages</Typography>
                </Stack>
              </Stack>
              <Stack flexDirection={'row'} alignItems={'center'} gap={5}>
                <Typography>Monitor Outgoing Messages: </Typography>
                <Stack
                  flexDirection="row"
                  component="label"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography>Headers Only</Typography>
                  <Controller
                    name={'fullOutgoing'}
                    control={control}
                    render={({ field }) => {
                      return (
                        <Switch
                          onChange={() => {
                            if (field.value === 'FULL_MESSAGE') {
                              setValue('fullOutgoing', 'HEADER_ONLY');
                            } else {
                              setValue('fullOutgoing', 'FULL_MESSAGE');
                            }
                          }}
                          checked={field.value === 'FULL_MESSAGE'}
                        />
                      );
                    }}
                  />
                  <Typography>Full Messages</Typography>
                </Stack>
              </Stack>
              <Stack flexDirection={'row'} alignItems="center" gap={2}>
                <Typography>Monitor Drafts:</Typography>
                <Controller
                  name={'draftMonitoring'}
                  control={control}
                  render={({ field }) => (
                    <TextField select {...field} sx={{ width: 200 }}>
                      {monitorDraftOptions.map((option) => (
                        <MenuItem
                          value={option.value}
                          key={option.displayValue}
                        >
                          {option.displayValue}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
              <Stack flexDirection={'row'} gap={2} alignItems="center">
                <Typography>Monitor Classic Hangout Chats:</Typography>
                <Controller
                  name={'chatMonitoring'}
                  control={control}
                  render={({ field }) => (
                    <TextField select {...field} sx={{ width: 200 }}>
                      {monitorChatOptions.map((option) => (
                        <MenuItem value={option.value} key={option.value}>
                          {option.displayValue}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
              <Alert severity="info" sx={{ mt: -1, mb: 2 }}>
                This setting only applies to classic Hangouts Chat, not Google
                Chat. Hangouts Chat is deprecated.
              </Alert>
            </Stack>
            <ChangeLogDialog
              open={openChangeLogDialog}
              handleClose={() => setOpenChangeLogDialog(false)}
              changeLogMessage={changeLogMessage}
              setChangeLogMessage={setChangeLogMessage}
              mutate={() =>
                dialogType === 'create'
                  ? mutate({
                      listeningUser: getValues('listeningUser'),
                      monitoredUser: userEmail,
                      requestId: getValues('listeningUser').split('@')[0],
                      startDay: getValues('dateRange.0')!.date(),
                      startMonth: getValues('dateRange.0')!.month(),
                      startYear: getValues('dateRange.0')!.year(),
                      endDay: getValues('dateRange.1')!.date(),
                      endMonth: getValues('dateRange.1')!.month(),
                      endYear: getValues('dateRange.1')!.year(),
                      draftMonitoring: getValues('draftMonitoring'),
                      chatMonitoring: getValues('chatMonitoring'),
                      fullIncoming: getValues('fullIncoming'),
                      fullOutgoing: getValues('fullOutgoing'),
                    })
                  : editMutate({
                      listeningUser: getValues('listeningUser'),
                      monitoredUser: getValues('monitoredUser'),
                      requestId: getValues('listeningUser').split('@')[0],
                      startDay: getValues('dateRange.0')!.date(),
                      startMonth: getValues('dateRange.0')!.month(),
                      startYear: getValues('dateRange.0')!.year(),
                      endDay: getValues('dateRange.1')!.date(),
                      endMonth: getValues('dateRange.1')!.month(),
                      endYear: getValues('dateRange.1')!.year(),
                      draftMonitoring: getValues('draftMonitoring'),
                      chatMonitoring: getValues('chatMonitoring'),
                      fullIncoming: getValues('fullIncoming'),
                      fullOutgoing: getValues('fullOutgoing'),
                    })
              }
              mutateLoading={isPending || editPending}
            />
          </Box>
        </>
      }
      primaryButton={{
        title: dialogType === 'create' ? 'Add' : 'Update',
        action: handleSubmit(onSubmit),
        loading: isPending || editPending,
        disabled: !isDirty,
      }}
    />
  );
}
